import type {Dispatcher} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import type {CopilotChatMessage} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'

import {CitationMarkerFilter, filterCitationMarkers} from './citation-marker-filter'

export class V1CitationMarkerSanitizer {
  private streamFilter?: CitationMarkerFilter
  private readonly dispatch: Dispatcher
  private readonly enabledForRuntime: boolean

  constructor(dispatch: Dispatcher, enabledForRuntime: boolean) {
    this.dispatch = dispatch
    this.enabledForRuntime = enabledForRuntime
  }

  startStream() {
    this.finishStream()
    this.streamFilter = this.enabledForRuntime ? new CitationMarkerFilter() : undefined
  }

  pushStreamingText(text: string): string {
    return this.streamFilter?.push(text) ?? text
  }

  finishStream() {
    const filter = this.streamFilter
    if (!filter) return

    this.streamFilter = undefined
    const {text, stats} = filter.finish()
    if (text) {
      this.dispatch({type: 'MESSAGE_STREAMING_TOKEN_ADDED', token: text})
    }

    if (stats.completeEnvelopeCount || stats.incompleteEnvelopeCount) {
      sendEvent('dotcom_chat.citation_marker_filtered', {
        source: 'stream',
        completeEnvelopeCount: stats.completeEnvelopeCount,
        incompleteEnvelopeCount: stats.incompleteEnvelopeCount,
      })
    }
  }

  sanitizeLoadedMessages(messages: CopilotChatMessage[]): CopilotChatMessage[] {
    if (!this.enabledForRuntime) return messages

    let changedMessageCount = 0
    const filteredMessages = messages.map(message => {
      if (message.role !== 'assistant') return message

      let changed = false
      const filteredContent = message.content === undefined ? undefined : filterCitationMarkers(message.content).text
      if (filteredContent !== message.content) changed = true

      const filteredContentParts = message.contentParts?.map(part => {
        if (part.type !== 'text') return part
        const content = filterCitationMarkers(part.content).text
        if (content === part.content) return part
        changed = true
        return {...part, content}
      })

      if (!changed) return message
      changedMessageCount++
      return {...message, content: filteredContent, contentParts: filteredContentParts}
    })

    if (changedMessageCount) {
      sendEvent('dotcom_chat.citation_marker_filtered', {
        source: 'history',
        changedMessageCount,
      })
    }

    return filteredMessages
  }
}
